import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Loader2, Sparkles, ExternalLink, Lock, Search, AlertTriangle, ScanText } from "lucide-react";
import axios from "axios";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Newseze Lens — iframe any URL, select any passage, get a Newseze score.
 *
 * Flow:
 * 1. User enters or arrives with a URL.
 * 2. We attempt to iframe the page (best effort; many sites set X-Frame-Options).
 * 3. User selects text in the iframe + clicks "Analyze selection" — which
 *    reads document.getSelection() from the parent (cross-origin restricted)
 *    OR the user pastes the snippet manually into the side panel.
 * 4. We POST to /api/reader/paste-analyze with the snippet → returns scores.
 * 5. Optional fallback: "Score the whole page" hits paste-analyze with the URL.
 *
 * Note: Cross-origin iframe text-selection is blocked by browser security
 * — that's not a Newseze bug, it's a browser policy. So we ALWAYS offer the
 * paste-the-snippet panel as the primary input, with the iframe as the
 * convenience preview.
 */
export default function Lens() {
  const [params, setParams] = useSearchParams();
  const [url, setUrl] = useState(params.get("url") || "");
  const [iframeUrl, setIframeUrl] = useState(params.get("url") || "");
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [snippet, setSnippet] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const iframeRef = useRef(null);

  // Detect iframe load failure (X-Frame-Options or CSP)
  useEffect(() => {
    if (!iframeUrl) return;
    setIframeBlocked(false);
    const timer = setTimeout(() => {
      // If contentDocument is null after 4s, the page very likely refused
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc === null || doc === undefined) setIframeBlocked(true);
      } catch { setIframeBlocked(true); }
    }, 4000);
    return () => clearTimeout(timer);
  }, [iframeUrl]);

  const openUrl = (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    const cleaned = url.trim();
    setIframeUrl(cleaned);
    setParams({ url: cleaned });
    setResult(null);
    setErr("");
  };

  const analyze = async (payload) => {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const email = getStoredEmail();
      const body = email ? { ...payload, email } : payload;
      const r = await axios.post(`${API}/reader/paste-analyze`, body, { timeout: 60000 });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Newseze couldn't analyze that — try a different snippet or URL.");
    } finally { setBusy(false); }
  };

  const analyzeSnippet = () => {
    if (snippet.trim().length < 40) {
      setErr("Paste at least 40 characters of the passage you want scored.");
      return;
    }
    analyze({ text: snippet.trim() });
  };

  const analyzeWholePage = () => {
    if (!iframeUrl) return;
    analyze({ url: iframeUrl });
  };

  // Convenience: try to pull current selection from same-origin iframe (will
  // silently fail cross-origin — that's expected and we surface paste fallback)
  const grabSelection = () => {
    try {
      const sel = iframeRef.current?.contentWindow?.getSelection?.();
      const text = sel?.toString?.()?.trim();
      if (text && text.length > 20) {
        setSnippet(text);
        return text;
      }
    } catch {}
    // Fallback: parent selection
    const text2 = window.getSelection?.()?.toString?.()?.trim();
    if (text2 && text2.length > 20) {
      setSnippet(text2);
      return text2;
    }
    setErr("Couldn't read your selection. Most websites block cross-site reads — please copy the passage and paste it into the snippet box on the right.");
    return "";
  };

  return (
    <>
      <Header />
      <main className="container mx-auto max-w-[1400px] px-4 py-6" data-testid="lens-page">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--gold)] bg-[#1d1303] px-2 py-1 rounded">
            <ScanText size={11} className="inline mr-1" /> Newseze Lens
          </span>
          <h1 className="text-[22px] font-black tracking-[-0.02em]">Iframe any page. Select any passage. Get the Newseze score.</h1>
        </div>
        <p className="text-[13px] text-[var(--muted)] mb-4">
          Powered by Newseze's algorithm. Drop in a URL, preview the page, select the part you want analyzed, and we'll score it.
          Most sites block cross-frame text reads — when that happens, just copy &amp; paste the snippet into the panel on the right.
        </p>

        <form onSubmit={openUrl} className="flex flex-wrap gap-2 mb-4" data-testid="lens-url-form">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste any URL — news article, X post, blog, magazine…"
            className="flex-1 min-w-[280px] border border-[var(--line)] rounded-md p-2.5 text-[13.5px]"
            data-testid="lens-url-input"
          />
          <button type="submit" className="btn primary" data-testid="lens-open-btn">
            <Search size={14} /> Open in Lens
          </button>
          {iframeUrl && (
            <a href={iframeUrl} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
               className="btn" data-testid="lens-open-tab">
              <ExternalLink size={13} /> Open in new tab
            </a>
          )}
        </form>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
          {/* Iframe column */}
          <div className="rounded-xl border border-[var(--line)] bg-white overflow-hidden relative min-h-[600px]" data-testid="lens-iframe-wrap">
            {!iframeUrl && (
              <div className="flex flex-col items-center justify-center h-[600px] text-[var(--muted)]">
                <ScanText size={48} className="mb-3 opacity-30" />
                <p className="text-[14px]">Paste a URL above to preview the page here.</p>
              </div>
            )}
            {iframeUrl && (
              <>
                <iframe
                  ref={iframeRef}
                  src={iframeUrl}
                  title="Newseze Lens preview"
                  className="w-full h-[640px] border-0"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                  referrerPolicy="no-referrer"
                  data-testid="lens-iframe"
                />
                {iframeBlocked && (
                  <div className="absolute inset-x-4 top-4 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-[12.5px] p-3 flex items-start gap-2" data-testid="lens-blocked-notice">
                    <Lock size={14} className="mt-0.5 shrink-0" />
                    <div>
                      <strong>This site blocks iframe embedding</strong> (its X-Frame-Options header forbids it).
                      Open it in a new tab, copy the passage you want scored, and paste it into the snippet panel on the right.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Analysis panel */}
          <aside className="space-y-3">
            <div className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid="lens-snippet-panel">
              <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-2 flex items-center gap-1">
                <Sparkles size={11} /> Analyze a passage
              </div>
              <p className="text-[12px] text-[var(--muted)] mb-2">
                Select text inside the iframe and click "Grab selection" — or paste the snippet directly.
              </p>
              <button onClick={grabSelection} type="button" className="btn small mb-2" data-testid="lens-grab-selection">
                <ScanText size={12} /> Grab selection
              </button>
              <textarea
                value={snippet}
                onChange={(e) => setSnippet(e.target.value)}
                placeholder="Paste the passage you want scored here…"
                className="w-full border border-[var(--line)] rounded-md p-2 text-[13px]"
                rows={6}
                maxLength={6000}
                data-testid="lens-snippet-input"
              />
              <div className="text-[10px] text-[var(--muted)] mb-2 text-right">{snippet.length}/6000</div>
              <button onClick={analyzeSnippet} disabled={busy || snippet.trim().length < 40}
                      className="btn primary w-full justify-center disabled:opacity-50"
                      data-testid="lens-analyze-snippet">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                Analyze this passage
              </button>
              <button onClick={analyzeWholePage} disabled={busy || !iframeUrl}
                      className="btn w-full justify-center mt-2 disabled:opacity-50"
                      data-testid="lens-analyze-page">
                <ExternalLink size={12} /> Score the whole page instead
              </button>
              {err && (
                <div className="text-[12px] text-rose-700 italic mt-2 flex items-start gap-1" data-testid="lens-error">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {err}
                </div>
              )}
            </div>

            {result && (
              <div className="rounded-xl border border-[var(--gold)] bg-white p-4" data-testid="lens-result">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] mb-2">Newseze result</div>
                <h3 className="text-[16px] font-black leading-tight mb-2" data-testid="lens-result-title">
                  {result.newseze_title || "Scored"}
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-3 text-[12px]">
                  <ScorePill label="Trust" value={result.trust_score} color="emerald" />
                  <ScorePill label="Drama" value={result.drama_score} color="rose" inverse />
                  <ScorePill label="Respect" value={result.respect_score} color="sky" />
                  <ScorePill label="Conspiracy" value={result.conspiracy_score} color="amber" inverse />
                </div>
                <p className="text-[12.5px] text-[var(--ink)] whitespace-pre-wrap leading-snug" data-testid="lens-result-narrative">
                  {result.narrative}
                </p>
                {result.share_id && (
                  <a href={`/scored/${result.share_id}`} className="btn small gold mt-3 w-full justify-center"
                     data-testid="lens-share-link">
                    Share this Newseze score
                  </a>
                )}
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ScorePill({ label, value, color, inverse }) {
  const v = typeof value === "number" ? value : 0;
  const pct = Math.max(0, Math.min(100, v));
  // Lower is good when `inverse` (e.g., drama/conspiracy)
  const colorCls = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    rose: "bg-rose-100 text-rose-800 border-rose-300",
    sky: "bg-sky-100 text-sky-800 border-sky-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
  }[color];
  return (
    <div className={`rounded-md border px-2 py-1.5 ${colorCls}`} data-testid={`lens-score-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.14em] font-black">{label}</span>
        <span className="text-[14px] font-black">{pct}</span>
      </div>
    </div>
  );
}
